import { sapienAxios } from "@/inertia-utils/hooks";
import { ContentBlockShape, ContentBlockType } from "@/models";
import { useMutation } from "@tanstack/react-query";
import { useContentBlockState } from "@/Pages/DesignStudio/Design/useContentBlockState";
import { useIsAddingContentBlock } from "../content-block-editing/useIsAddingContentBlock";
import { atom, useAtom } from "jotai";
import { useCallback } from "react";

const isSavingIdAtom = atom("");
export const updatedContentBlockIdAtom = atom<string | false>(false);
updatedContentBlockIdAtom.debugLabel = "updatedContentBlockIdAtom";

// function getBlockWithNoParent(
//     block: ContentBlockShape,
//     contentBlocks: ContentBlockShape[],
// ) {
//     if (block.parent_content_block_id) {
//         const parentBlock = contentBlocks.find(
//             ({ id }) => id === block.parent_content_block_id,
//         );
//         return getBlockWithNoParent(parentBlock, contentBlocks);
//     }
//     return block;
// }

type WeightUpdate = {
    id: string;
    weight: number;
};

type SaveContentBlockResponse = {
    savedContentBlock: ContentBlockShape;
    weightUpdates: WeightUpdate[];
};

export async function saveContentBlock(contentBlock: any) {
    if (contentBlock.id) {
        const { data } = await sapienAxios.put<
            SaveContentBlockResponse,
            "creator.design.content-blocks.nested.update"
        >(
            "creator.design.content-blocks.nested.update",
            {
                ...contentBlock,
                position: "after",
                theme: JSON.stringify(contentBlock.theme),
            },
            {
                contentBlock: contentBlock.id,
            },
        );
        const { savedContentBlock, weightUpdates } = data;
        return { savedContentBlock, weightUpdates };
    } else {
        const { data } = await sapienAxios.post<
            SaveContentBlockResponse,
            "creator.design.content-blocks.nested.store"
        >("creator.design.content-blocks.nested.store", {
            ...contentBlock,
            position: "after",
        });
        const { savedContentBlock, weightUpdates } = data;
        return { savedContentBlock, weightUpdates };
    }
}

export function useUpdateStyles() {
    // const { setContentBlock } = useContentBlockState();

    return useMutation({
        mutationFn: async (contentBlock: ContentBlockShape) => {
            // setContentBlock(contentBlock);
            saveContentBlock(contentBlock);
        },
        onError: (error) => {
            console.error("Error saving content block", error);
        },
    });
}

export function useSaveContentBlock() {
    const { setContentBlock } = useContentBlockState();
    const { setIsAddingBlock } = useIsAddingContentBlock();
    const [_, setUpdatedContentBlockId] = useAtom(updatedContentBlockIdAtom);
    const { contentBlocks: storedBlocks } = useContentBlockState();
    const [idOfSavingBlock, setIdOfSavingBlock] = useAtom(isSavingIdAtom);

    const saveBlock = useCallback(
        (contentBlock: ContentBlockShape) => {
            if (
                !contentBlock.parent_content_block_id &&
                contentBlock.content_block_type !== ContentBlockType.Page
            ) {
                const page = Object.values(storedBlocks).find(
                    (block) =>
                        block.content_block_type === ContentBlockType.Page,
                );
                contentBlock.parent_content_block_id = page?.id;
            }

            setIdOfSavingBlock(contentBlock.id);
            return saveContentBlock(contentBlock);
        },
        [storedBlocks,],
    );

    const { mutate, mutateAsync, isPending } = useMutation({
        mutationFn: saveBlock,
        onSuccess: ({ weightUpdates, savedContentBlock }) => {
            setUpdatedContentBlockId(false);
            setIsAddingBlock(false);
            
            // Apply weight updates to all affected siblings
            weightUpdates?.forEach(({ id, weight }) => {
                if (storedBlocks[id] && storedBlocks[id].weight !== weight) {
                    setContentBlock({ ...storedBlocks[id], weight });
                }
            });

            // Handle the saved block itself
            if (!storedBlocks[savedContentBlock.id]) {
                // New block - add it to the store
                setContentBlock(savedContentBlock);
            } else if (savedContentBlock?.contentBlocks?.length) {
                // Existing block with children - update without overwriting nested blocks
                setContentBlock({...savedContentBlock, contentBlocks:[]});
            } else {
                // Existing leaf block - update normally
                setContentBlock(savedContentBlock);
            }
            
            setIdOfSavingBlock("");
            return savedContentBlock;
        },
        onError: (error) => {
            console.error("Error saving content block", error);
        },
    });

    return {    
        mutate,
        mutateAsync,
        isPending,
        idOfSavingBlock,
    }
}
 